import { motion } from 'framer-motion';
import { ArrowRight } from '@phosphor-icons/react';
import { scrollToId } from '@/components/site/Navbar';

/**
 * CapabilitySection — alternating dark/light section showing
 * overline, title, body, optional secondary body, feature cards grid, image, CTA.
 *
 * Props:
 *  id, testId, theme ('dark'|'light'), imageSide ('left'|'right'),
 *  image, imageAlt, overline, title, body, body2, cards [{title, desc, Icon}],
 *  ctaLabel, ctaTestId, code (e.g. '02')
 */
export default function CapabilitySection({
  id,
  testId,
  theme = 'dark',
  imageSide = 'right',
  image,
  imageAlt,
  overline,
  title,
  body,
  body2,
  cards = [],
  ctaLabel,
  ctaTestId,
  code,
}) {
  const isDark = theme === 'dark';
  const bg = isDark ? 'bg-[#0A1128]' : 'bg-[#F1F5F9]';
  const text = isDark ? 'text-white' : 'text-[#0F172A]';
  const mutedText = isDark ? 'text-[#94A3B8]' : 'text-[#475569]';
  const cardBg = isDark ? 'bg-[#151F32] border-white/10' : 'bg-white border-black/10';
  const cardHover = isDark ? 'hover:border-[#4F6355]/60' : 'hover:border-[#4F6355]';
  const iconWrap = isDark
    ? 'border-white/15 bg-white/5 text-[#4F6355]'
    : 'border-black/10 bg-[#0A1128]/5 text-[#4F6355]';
  const lineColor = isDark ? 'bg-[#4F6355]' : 'bg-[#4F6355]';
  const overlineColor = 'text-[#4F6355]';
  const buttonClass = isDark ? 'btn-outline-dark' : 'btn-outline-light';

  return (
    <section
      id={id}
      data-testid={testId}
      className={`relative ${bg} ${text} overflow-hidden`}
    >
      <div className={`absolute inset-0 ${isDark ? 'bg-grid-lines' : 'bg-grid-lines-light'} opacity-60 pointer-events-none`} />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start ${imageSide === 'left' ? 'lg:[&>div:first-child]:order-1' : ''}`}>
          {/* Image column */}
          <div className={`lg:col-span-5 ${imageSide === 'left' ? 'lg:order-1' : 'lg:order-2'}`}>
            <div className="sticky top-28">
              <div className="relative aspect-[4/5] overflow-hidden border border-white/10">
                <img src={image} alt={imageAlt} className="w-full h-full object-cover" />
                <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-t from-[#0A1128] via-[#0A1128]/30 to-transparent' : 'bg-gradient-to-t from-[#0A1128]/40 via-transparent to-transparent'}`} />
                <div className="absolute top-4 left-4 right-4 flex items-center justify-between font-mono text-[10px] tracking-[0.25em] uppercase text-white/80">
                  <span>{code ? `CAP/${code}` : 'CAP'}</span>
                  <span className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#4F6355] rounded-full" />
                    LIVE
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Content column */}
          <div className={`lg:col-span-7 ${imageSide === 'left' ? 'lg:order-2' : 'lg:order-1'}`}>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.6 }}
              className="flex items-center gap-3"
            >
              <span className={`w-8 h-px ${lineColor}`} />
              <span className={`text-[11px] tracking-[0.3em] uppercase font-bold ${overlineColor}`}>
                {overline}
              </span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.7, delay: 0.05 }}
              className={`font-display font-bold tracking-tighter mt-6 text-3xl sm:text-4xl lg:text-5xl leading-[1.05] ${text}`}
            >
              {title}
            </motion.h2>

            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.7, delay: 0.12 }}
              className={`mt-6 text-base md:text-lg leading-relaxed ${mutedText} max-w-2xl`}
            >
              {body}
            </motion.p>

            {body2 && (
              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className={`mt-4 text-sm md:text-base leading-relaxed ${mutedText} max-w-2xl opacity-90`}
              >
                {body2}
              </motion.p>
            )}

            {/* Cards grid */}
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {cards.map((c, idx) => {
                const Icon = c.Icon;
                return (
                  <motion.div
                    key={c.title + idx}
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.5, delay: idx * 0.05 }}
                    className={`group relative p-5 md:p-6 border ${cardBg} ${cardHover} transition-all duration-300 hover:-translate-y-1`}
                  >
                    <div className={`w-10 h-10 flex items-center justify-center border ${iconWrap}`}>
                      {Icon ? <Icon size={20} weight="duotone" /> : null}
                    </div>
                    <h3 className={`mt-4 font-display font-semibold text-base md:text-lg tracking-tight ${text}`}>
                      {c.title}
                    </h3>
                    <p className={`mt-2 text-xs md:text-sm leading-relaxed ${mutedText}`}>{c.desc}</p>
                  </motion.div>
                );
              })}
            </div>

            <div className="mt-10">
              <button
                data-testid={ctaTestId}
                onClick={() => scrollToId('contact')}
                className={`${buttonClass} inline-flex items-center gap-3 px-6 py-3.5 text-[11px] tracking-[0.22em] uppercase font-bold`}
              >
                {ctaLabel}
                <ArrowRight size={14} weight="bold" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
